import { FormControl, FormField, HeadlessFormItem } from "@/components/ui/form";
import {
  SelectFetcher,
  SelectFetcherProps,
} from "@/components/ui/select-fetcher";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PartialFormFieldProps } from "./AbstractForm";
import { Label } from "@/components/ui/label";
import {
  MaskableFieldHeader,
  useMaskableFieldExpanded,
} from "./MaskableFieldHeader";
import type { MaskableFieldProps } from "./MaskedFormFieldShell";
import { cn } from "@/lib/utils";

export const AbstractFormSelectFetcher = <T,>({
  name,
  field,
  label,
  nonMaskable,
  maskable,
  ...props
}: SelectFetcherProps<T> & {
  name: string;
  label?: string;
  field?: PartialFormFieldProps;
} & MaskableFieldProps) => {
  const form = useFormContext();
  const { t } = useTranslation();
  const { enabled: maskEnabled, showControl, minimized } =
    useMaskableFieldExpanded(name, {
      nonMaskable,
      maskable,
    });

  // Masked → hidden entirely (re-enable it from the field visibility menu).
  if (maskEnabled && !nonMaskable && minimized) {
    return null;
  }

  return (
    <FormField
      name={name}
      {...form}
      {...field}
      render={({ field }) => (
        <HeadlessFormItem>
          <FormControl>
            <div className={cn("flex flex-col", maskEnabled ? "gap-1.5" : "gap-0")}>
              {maskEnabled ? (
                <MaskableFieldHeader
                  fieldName={name}
                  nonMaskable={nonMaskable}
                  maskable={maskable}
                  className="mb-0"
                  label={label ?? t(name)}
                />
              ) : (
                <Label
                  htmlFor={name}
                  className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                >
                  {label ?? t(name)}
                </Label>
              )}
              <div className={cn(maskEnabled && !showControl && "hidden")}>
                <SelectFetcher
                  triggerPlaceholder={label ?? t(name)}
                  value={field.value}
                  onValueChange={field.onChange}
                  {...props}
                />
              </div>
            </div>
          </FormControl>
        </HeadlessFormItem>
      )}
    />
  );
};
